import React, { useState } from 'react';
import Navigation from '../components/Navigation';
import ContactForm from '../components/ContactForm';
import FAQ from '../components/FAQ';
import SEO from '../components/SEO';
import { organizationSchema } from '../utils/schemas';
import CostcoFooter from '../components/CostcoFooter';
import { shopCardValue as shopCardValueFor } from '../lib/shopCardScale';
import { Gift, Home, Car, Briefcase, CreditCard, CheckCircle, ArrowRight, Calculator, Calendar, Award, HelpCircle } from 'lucide-react';

const DrawnDownTooltip = () => {
  const [show, setShow] = useState(false);
  return (
    <span className="relative inline-block">
      <button
        type="button"
        onClick={() => setShow(!show)}
        className="inline-flex items-center space-x-1 text-[#00594C] underline decoration-dotted cursor-pointer font-semibold"
      >
        <span>drawn-down</span>
        <HelpCircle className="w-3.5 h-3.5" />
      </button>
      {show && (
        <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-72 bg-[#00594C] text-white text-xs rounded-lg p-3 shadow-lg z-50">
          <strong>Drawn-down amount</strong> means the portion of your approved loan that has actually been used. It excludes any funds sitting in offset accounts, redraw facilities, or amounts that haven't been drawn. For example, a $600,000 loan with $60,000 in offset has a drawn-down amount of $540,000.
          <span className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-[#00594C]" />
        </span>
      )}
    </span>
  );
};

const formatCalcAmount = (value) => {
  const numbers = value.replace(/[^0-9]/g, '');
  if (!numbers) return '';
  return Number(numbers).toLocaleString('en-AU');
};

const CostcoLanding = () => {
  const [loanAmount, setLoanAmount] = useState('');
  const [loanType, setLoanType] = useState('home');

  const handleLoanAmountChange = (e) => {
    const formatted = formatCalcAmount(e.target.value);
    setLoanAmount(formatted);
  };

  // Derived on every render, so the displayed value can never lag the inputs.
  const amount = parseFloat(loanAmount.replace(/,/g, ''));
  const hasAmount = Number.isFinite(amount) && amount > 0;
  const shopCardValue = hasAmount ? shopCardValueFor(loanType, amount) : null;

  const costcoFaqs = [
    {
      question: 'Who is eligible for a Costco Shop Card?',
      answer: 'You must be a current Costco Executive Member both at the time you apply for your loan and when the Shop Card is issued. The Shop Card is only paid on loans arranged by Manage Your Loans that are approved, settled, and drawn down.',
    },
    {
      question: 'How much will my Shop Card be worth?',
      answer: 'The value is based on the actual drawn-down amount of your settled loan. Residential and commercial mortgages earn $200 per $100,000 bracket up to a maximum of $4,000. Car, asset, and personal finance use a separate scale. Use the calculator above for an estimate — your final Shop Card is based on the drawn-down loan amount at settlement.',
    },
    {
      question: 'What does "drawn-down amount" mean?',
      answer: 'The drawn-down amount is the portion of your approved loan that has actually been used. Funds sitting in offset accounts, redraw facilities, or amounts that have not been drawn are excluded. For example, a $600,000 loan with $60,000 in offset has a drawn-down amount of $540,000, and your Shop Card is calculated on the $540,000.',
    },
    {
      question: 'When and how do I receive my Shop Card?',
      answer: 'The Costco Shop Card is delivered electronically as a digital card via email from the Costco Services Team within 90 days of your loan settling. It has no expiry date and can be used like cash at any Costco warehouse or online at Costco.com.au, but it cannot be exchanged for cash.',
    },
    {
      question: 'Does the Shop Card cost me anything or change my loan?',
      answer: 'No. There is no cost to you for using a Manage Your Loans broker, and the Shop Card does not change your interest rate or loan terms. We compare a broad range of lenders to find a competitive option suited to your needs — the Shop Card is an additional benefit for eligible Costco Executive Members on settled loans.',
    },
    {
      question: 'What happens if I refinance later?',
      answer: 'You will not receive a Shop Card if the loan is not approved, not settled, or not fully drawn down. If a loan is refinanced before the second anniversary of the original loan settled by Manage Your Loans — for which you have already received a Shop Card — a further Shop Card will not be payable on that refinance. Full details are in the Terms & Conditions below.',
    },
    {
      question: 'Is Manage Your Loans affiliated with Costco?',
      answer: 'Manage Your Loans Pty Ltd (CR 540988) operates under Home Mortgage Plus Pty Ltd (ACL 392200) and has a referral arrangement with Costco. The two businesses are not otherwise affiliated. For Costco Shop Card terms, visit www.costco.com.au/services.',
    },
  ];

  const services = [
    {
      icon: Home,
      title: 'Home Loans',
      description: 'First home buyers, next home, refinancing, or investment properties',
      benefits: ['Low deposit options', 'Competitive rates', 'Fast pre-approval']
    },
    {
      icon: Car,
      title: 'Car & Asset Finance',
      description: 'New or used vehicles, equipment, and business assets',
      benefits: ['Flexible terms', 'Quick approval', 'Trade-in assistance']
    },
    {
      icon: Briefcase,
      title: 'Business Loans',
      description: 'Commercial property, working capital, and business expansion',
      benefits: ['Tailored solutions', 'Expert guidance', 'Growth support']
    },
    {
      icon: CreditCard,
      title: 'Personal Loans',
      description: 'Debt consolidation, renovations, holidays, and more',
      benefits: ['Fast approval', 'No hidden fees', 'Flexible repayments']
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO title="Costco Member Loan Partner" description="Exclusive loan solutions for Costco Executive Members. Get tailored home loans plus Costco Shop Cards on settled loans." path="/costco" schema={organizationSchema()} />
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-[#00594C] to-[#003d35] text-white overflow-hidden">
        {/* Costco Logo Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 right-20 w-32 h-32">
            <Gift className="w-full h-full text-[#99DF63]" />
          </div>
          <div className="absolute bottom-20 left-10 w-40 h-40">
            <Gift className="w-full h-full text-[#99DF63]" />
          </div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <img
              src="/images/costco-logo.png"
              alt="Costco Wholesale"
              className="h-16 mx-auto mb-6"
            />
            <div className="inline-flex items-center space-x-3 bg-white/10 backdrop-blur-md px-6 py-3 rounded-full mb-6 border border-white/20">
              <Award className="w-6 h-6 text-[#99DF63]" />
              <span className="font-semibold text-lg">Exclusive Costco Member Benefits</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-bold mb-6" data-testid="costco-hero-title">
              Your Exclusive
              <span className="text-[#99DF63] block">Costco Loan Partner</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
              As Costco's exclusive finance partner, we offer tailored loan solutions PLUS Costco Shop Cards for Executive Members on settled loans! <a href="/costco-terms" className="underline hover:text-[#99DF63] transition-colors">Terms and conditions apply</a>.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#calculator"
                className="inline-flex items-center justify-center space-x-2 bg-[#99DF63] text-[#00594C] px-8 py-4 rounded-full font-bold text-lg hover:bg-[#8BCF52] transition-all shadow-lg"
                data-testid="hero-cta-calculator"
              >
                <Calculator className="w-5 h-5" />
                <span>Calculate Your Shop Card</span>
              </a>
              <a
                href="#book-appointment"
                className="inline-flex items-center justify-center space-x-2 bg-white/10 backdrop-blur-md text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white/20 transition-all border border-white/30"
                data-testid="hero-cta-book"
              >
                <Calendar className="w-5 h-5" />
                <span>Book Appointment</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* What is the Costco Shop Card */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <img
                src="/images/costco-logo.png"
                alt="Costco Wholesale"
                className="h-12 mx-auto mb-6"
              />
              <div className="inline-flex items-center space-x-2 bg-[#99DF63]/10 px-6 py-2 rounded-full mb-6">
                <Gift className="w-5 h-5 text-[#00594C]" />
                <span className="text-[#00594C] font-semibold">Executive Member Benefit</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-[#00594C] mb-6">
                What is the Costco Shop Card?
              </h2>
            </div>

            <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg border-2 border-gray-100">
              <div className="space-y-6 text-lg text-gray-700">
                <p className="leading-relaxed">
                  The <strong>Costco Shop Card</strong> is a digital gift card that works just like cash at any Costco warehouse or online at Costco.com.au. Costco passes on the referral commission to eligible Executive Members in the form of a Costco Shop Card.
                </p>
                
                <div className="bg-[#99DF63]/10 rounded-xl p-6 border-l-4 border-[#99DF63]">
                  <h3 className="font-bold text-xl text-[#00594C] mb-3">How It Works:</h3>
                  <ol className="space-y-3 list-decimal list-inside">
                    <li><strong>Apply for a loan</strong> with your valid Costco Executive Membership number</li>
                    <li><strong>Settle your loan</strong> with our help</li>
                    <li><strong>Receive your Shop Card</strong> via email within 90 days of settlement</li>
                    <li><strong>Shop at Costco</strong> using your Shop Card like cash!</li>
                  </ol>
                </div>

                <div className="grid md:grid-cols-3 gap-6 mt-8">
                  <div className="text-center p-6 bg-gray-50 rounded-xl">
                    <CheckCircle className="w-12 h-12 text-[#99DF63] mx-auto mb-3" />
                    <h4 className="font-bold text-[#00594C] mb-2">No Expiry Date</h4>
                    <p className="text-sm text-gray-600">Use it whenever you want</p>
                  </div>
                  <div className="text-center p-6 bg-gray-50 rounded-xl">
                    <CheckCircle className="w-12 h-12 text-[#99DF63] mx-auto mb-3" />
                    <h4 className="font-bold text-[#00594C] mb-2">Digital Delivery</h4>
                    <p className="text-sm text-gray-600">Sent directly to your email</p>
                  </div>
                  <div className="text-center p-6 bg-gray-50 rounded-xl">
                    <CheckCircle className="w-12 h-12 text-[#99DF63] mx-auto mb-3" />
                    <h4 className="font-bold text-[#00594C] mb-2">Any Purchase</h4>
                    <p className="text-sm text-gray-600">Use on any Costco product</p>
                  </div>
                </div>

                <p className="text-sm text-gray-600 italic mt-6">
                  <strong>Important:</strong> You must be a Costco Executive Member at the time of application and when the Shop Card is issued. The Shop Card value is based on your actual <DrawnDownTooltip /> loan amount (excluding offset, redraw, or undrawn amounts).
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="py-20 bg-gray-50 scroll-mt-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <img
              src="/images/costco-logo.png"
              alt="Costco Wholesale"
              className="h-12 mx-auto mb-6"
            />
            <h2 className="text-4xl font-bold text-[#00594C] mb-4">
              Our Services for Costco Members
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive loan solutions with exclusive member benefits
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <div
                  key={index}
                  className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all"
                >
                  <div className="w-14 h-14 bg-[#99DF63]/10 rounded-full flex items-center justify-center mb-4">
                    <Icon className="w-7 h-7 text-[#00594C]" />
                  </div>
                  <h3 className="text-xl font-bold text-[#00594C] mb-3">{service.title}</h3>
                  <p className="text-gray-600 mb-4 text-sm">{service.description}</p>
                  <ul className="space-y-2">
                    {service.benefits.map((benefit, bIndex) => (
                      <li key={bIndex} className="flex items-center space-x-2 text-sm text-gray-700">
                        <CheckCircle className="w-4 h-4 text-[#99DF63] flex-shrink-0" />
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Shop Card Calculator */}
      <section id="calculator" className="py-20 bg-white scroll-mt-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-[#00594C] mb-4">
                Calculate Your Shop Card Value
              </h2>
              <p className="text-xl text-gray-600">
                See how much you could earn on your Costco Shop Card
              </p>
            </div>

            <div className="bg-gradient-to-br from-[#00594C] to-[#003d35] rounded-2xl p-8 shadow-xl text-white">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold mb-2">Loan Type</label>
                  <select
                    value={loanType}
                    onChange={(e) => setLoanType(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-white text-[#00594C] font-semibold focus:outline-none focus:ring-2 focus:ring-[#99DF63]"
                  >
                    <option value="home">Home & Business Loans</option>
                    <option value="car">Car & Asset Finance</option>
                    <option value="personal">Personal Loans</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">Loan Amount</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#00594C] font-bold">$</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={loanAmount}
                      onChange={handleLoanAmountChange}
                      placeholder="Enter loan amount"
                      className="w-full pl-8 pr-4 py-3 rounded-lg bg-white text-[#00594C] font-semibold focus:outline-none focus:ring-2 focus:ring-[#99DF63]"
                    />
                  </div>
                </div>

                {shopCardValue !== null && (
                  <div aria-live="polite" className="mt-6 bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20 text-center">
                    <p className="text-sm mb-2">Your Estimated Shop Card Value:</p>
                    {shopCardValue > 0 ? (
                      <p className="text-5xl font-bold text-[#99DF63]">${shopCardValue.toLocaleString()}*</p>
                    ) : (
                      <p className="text-xl font-bold text-[#99DF63]">No Shop Card at this loan amount</p>
                    )}
                    <p className="text-sm mt-3 text-gray-200">
                      Based on {loanType === 'home' ? 'Home/Business' : loanType === 'car' ? 'Car/Asset' : 'Personal'} loan of ${amount.toLocaleString()}
                    </p>
                    <p className="text-xs mt-2 text-gray-300">
                      * Based on fully drawn-down loan amount. Funds in offset or redraw are excluded from the Shop Card calculation.
                    </p>
                  </div>
                )}
              </div>
            </div>

            <p className="text-sm text-gray-500 text-center mt-6">
              *This is an estimate. Actual Shop Card value may vary based on final <DrawnDownTooltip /> loan amount.
            </p>
          </div>
        </div>
      </section>

      {/* Find Your Nearest Costco */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-[#00594C] mb-4">Find Your Nearest Costco</h2>
              <p className="text-gray-600">Explore loan options and property insights near your local Costco warehouse</p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              <div>
                <h3 className="font-bold text-[#00594C] mb-3">New South Wales</h3>
                <ul className="space-y-2">
                  <li><a href="/costco/auburn" className="text-gray-600 hover:text-[#99DF63] transition-colors">Costco Auburn (Lidcombe)</a></li>
                  <li><a href="/costco/casula" className="text-gray-600 hover:text-[#99DF63] transition-colors">Costco Crossroads (Casula)</a></li>
                  <li><a href="/costco/marsden-park" className="text-gray-600 hover:text-[#99DF63] transition-colors">Costco Marsden Park</a></li>
                  <li><a href="/costco/lake-macquarie" className="text-gray-600 hover:text-[#99DF63] transition-colors">Costco Lake Macquarie</a></li>
                </ul>
              </div>
              <div>
                <h3 className="font-bold text-[#00594C] mb-3">Victoria</h3>
                <ul className="space-y-2">
                  <li><a href="/costco/ardeer" className="text-gray-600 hover:text-[#99DF63] transition-colors">Costco Ardeer</a></li>
                  <li><a href="/costco/epping" className="text-gray-600 hover:text-[#99DF63] transition-colors">Costco Epping</a></li>
                  <li><a href="/costco/ringwood" className="text-gray-600 hover:text-[#99DF63] transition-colors">Costco Ringwood</a></li>
                  <li><a href="/costco/moorabbin" className="text-gray-600 hover:text-[#99DF63] transition-colors">Costco Moorabbin Airport</a></li>
                </ul>
                <h3 className="font-bold text-[#00594C] mb-3 mt-6">Queensland</h3>
                <ul className="space-y-2">
                  <li><a href="/costco/north-lakes" className="text-gray-600 hover:text-[#99DF63] transition-colors">Costco North Lakes</a></li>
                  <li><a href="/costco/ipswich" className="text-gray-600 hover:text-[#99DF63] transition-colors">Costco Ipswich (Bundamba)</a></li>
                  <li><a href="/costco/gold-coast" className="text-gray-600 hover:text-[#99DF63] transition-colors">Costco Gold Coast (Coomera)</a></li>
                </ul>
              </div>
              <div>
                <h3 className="font-bold text-[#00594C] mb-3">Western Australia</h3>
                <ul className="space-y-2">
                  <li><a href="/costco/perth" className="text-gray-600 hover:text-[#99DF63] transition-colors">Costco Perth Airport</a></li>
                  <li><a href="/costco/casuarina" className="text-gray-600 hover:text-[#99DF63] transition-colors">Costco Casuarina</a></li>
                </ul>
                <h3 className="font-bold text-[#00594C] mb-3 mt-6">ACT</h3>
                <ul className="space-y-2">
                  <li><a href="/costco/canberra" className="text-gray-600 hover:text-[#99DF63] transition-colors">Costco Canberra Airport</a></li>
                </ul>
                <h3 className="font-bold text-[#00594C] mb-3 mt-6">South Australia</h3>
                <ul className="space-y-2">
                  <li><a href="/costco/adelaide" className="text-gray-600 hover:text-[#99DF63] transition-colors">Costco Adelaide (Kilburn)</a></li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FAQ
        faqs={costcoFaqs}
        eyebrow="Costco Member FAQ"
        title="Costco Shop Card — Frequently Asked Questions"
        subtitle="Everything Costco Executive Members ask about earning a Shop Card on a settled loan"
      />

      {/* General Advice Warning */}
      <section className="py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white border border-amber-200 rounded-xl p-6">
            <p className="text-sm text-gray-600">
              <strong className="text-gray-800">General Advice Warning:</strong> The information provided is general in nature and does not take into account your objectives, financial situation or needs. You should consider whether it is appropriate for you.
            </p>
          </div>
        </div>
      </section>

      {/* Booking Form Section */}
      <div id="get-in-touch" />
      <ContactForm />

      {/* Terms & Conditions */}
      <section className="py-12 bg-gray-50" id="costco-terms">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-semibold text-[#00594C] mb-6">Terms & Conditions</h2>
            <div className="bg-white rounded-xl p-8 shadow-sm text-sm text-gray-600 space-y-4">
              <p>
                * Manage Your Loans Pty Ltd (MYL) ACN 652 442 790 Credit Representative (CR) 540988 provides access to credit services via Home Mortgage Plus Pty Ltd ACN 105 991 839, Australian Credit License (ACL) 392200. A referral arrangement is in place between MYL and Costco; however, they are not otherwise affiliated in any other way. For terms and conditions on Costco Executive Members Shop Cards please visit <a href="https://www.costco.com.au/services" target="_blank" rel="noopener noreferrer" className="text-[#00594C] underline">www.costco.com.au/services</a>.
              </p>
              <p>
                You will not receive a Shop Card if the loan is not approved, not settled, not fully drawn down (including balances sitting in offset or redraw), or if a new loan is refinanced before the second anniversary of the original loan settled by Manage Your Loans, for which you have already received a Shop Card. Additionally, the value of the Shop Card will be based on the drawn-down amount of the loan. For example, if you take out a $600,000 loan and the balance of the loan is $540,000 with $60,000 sitting in redraw or offset you will only receive a Shop Card to the value of $540,000.
              </p>
              <p>
                The Costco Shop Card has no expiration date and cannot be exchanged for cash. The Costco Shop Card will be delivered electronically as a digital card via email from the Costco Services Team within 90 days of the loan settlement.
              </p>
              <p>
                Costco Executive Membership must be active at the time of Shop Card issuance. For full terms and conditions, please visit the <a href="/costco-terms" className="text-[#00594C] underline">Costco Offer Terms and Conditions</a> page.
              </p>
            </div>
          </div>
        </div>
      </section>

      <CostcoFooter />
    </div>
  );
};

export default CostcoLanding;